// 12-fix-wave 7.4(d) — the operator's "reset and continue" answer, carried from the mapping screen
// (the "החלף מקור" dialog) through the picker and back, keyed by the mapping page's CONTAINER: its
// channel and its path id (`<channel>-<pathId>`, e.g. `1-1234` for an e-mail page, `3-1234` for a
// WhatsApp unit's page). The channel is part of the key because e-mail ids and WACampaignIDs are
// independent IDENTITY sequences: the same number names two different containers.
// While the key is '1' every save of that page sends `ConfirmReset: true`; the first save that
// carried it and answered 200 removes it. sessionStorage can throw (private mode, blocked storage),
// so every access is guarded and a failure reads as "not confirmed" — the server then answers 409
// SOURCE_RESET_CONFIRM_REQUIRED and the page asks again.
// QA BUG-29 — the key can also hold the TARGET source (a DataSourceID instead of '1'): the operator
// confirmed "continue with source X, reset the previous mapping" on the picker (Idan's R1). Such a key
// is NOT read by `isConfirmResetArmed`, so no ordinary save carries it; only the mapping page's entry
// check reads it (`confirmResetTarget`), and only for the page whose URL source is that X.

const KEY_PREFIX = 'smartsend.confirmReset.';

const validRef = (channel: number | null | undefined, pageId: number | null | undefined): boolean =>
    channel != null && channel > 0 && pageId != null && pageId > 0;

export const confirmResetKey = (channel: number, pageId: number): string => `${KEY_PREFIX}${channel}-${pageId}`;

export const isConfirmResetArmed = (channel: number | null | undefined, pageId: number | null | undefined): boolean => {
    if (!validRef(channel, pageId)) return false;
    try {
        return window.sessionStorage.getItem(confirmResetKey(channel as number, pageId as number)) === '1';
    } catch {
        return false;
    }
};

// QA BUG-31 r1 — ONE-SHOT MARKER of the «החלף מקור» → «אפס והמשך» trip to the picker: set by the mapping
// page just before it leaves, read AND removed by the picker on its next entry. Unlike the '1' key above
// (which lives until a save uses it, for BUG-29), it can never outlive that one picker entry. Outside the
// KEY_PREFIX namespace, so `armConfirmReset` does not sweep it. Unreadable storage ⇒ "no trip".
const TRIP_KEY = 'smartsend.changeSourceTrip';

export const markChangeSourceTrip = (): void => {
    try {
        window.sessionStorage.setItem(TRIP_KEY, '1');
    } catch {
        // storage unavailable: the picker then starts clean (the WhatsApp intent is reset)
    }
};

// QA BUG-32 — A CONFIRMATION IS VALID ONLY FOR THE IMMEDIATE NEXT STEP, never for a later visit.
// The '1' key and a target key live until a save uses them; a trip that was abandoned (the picker left
// by the menu, a failed container or GetMapping) left them armed for the whole tab session, and a LATER
// entry of the same campaign then reset its mapping with no dialog (R1) or skipped R2. So:
//   · the picker, on any entry that is not the change-source trip, drops every key (`clearAllConfirmResets`);
//   · the picker marks the mapping page it is opening (`markConfirmEntry`), and that page honours a key
//     only when its mount took that one-shot mark (`takeConfirmEntry`).
const ENTRY_KEY = 'smartsend.confirmEntry';

export const clearAllConfirmResets = (): void => {
    try {
        const keys: string[] = [];
        for (let i = 0; i < window.sessionStorage.length; i += 1) {
            const k = window.sessionStorage.key(i);
            if (k && k.indexOf(KEY_PREFIX) === 0) keys.push(k);
        }
        keys.forEach((k) => window.sessionStorage.removeItem(k));
    } catch {
        // nothing to clear
    }
};

export const markConfirmEntry = (pageId: number): void => {
    try {
        window.sessionStorage.setItem(ENTRY_KEY, String(pageId));
    } catch {
        // storage unavailable: the mapping page then asks instead of trusting a key
    }
};

// The e-mail page id the picker just opened, or null; removed on read.
export const takeConfirmEntry = (): number | null => {
    try {
        const v = window.sessionStorage.getItem(ENTRY_KEY);
        window.sessionStorage.removeItem(ENTRY_KEY);
        const n = Number(v);
        return v != null && Number.isInteger(n) && n > 0 ? n : null;
    } catch {
        return null;
    }
};

export const takeChangeSourceTrip = (): boolean => {
    try {
        const on = window.sessionStorage.getItem(TRIP_KEY) === '1';
        window.sessionStorage.removeItem(TRIP_KEY);
        return on;
    } catch {
        return false;
    }
};

// QA BUG-29 — the source the operator confirmed for this page on the picker, or null (no key, the
// plain '1' key, or unreadable storage).
export const confirmResetTarget = (channel: number | null | undefined, pageId: number | null | undefined): number | null => {
    if (!validRef(channel, pageId)) return null;
    try {
        const v = window.sessionStorage.getItem(confirmResetKey(channel as number, pageId as number));
        if (v == null || v === '1') return null;
        const n = Number(v);
        return Number.isInteger(n) && n > 0 ? n : null;
    } catch {
        return null;
    }
};

// ONE pending confirmation at a time: arming this page's key removes any other page's key left
// behind by a round trip that never came back (for example the picker opened another send).
// QA BUG-29 — `target` (a source id) stores that source instead of '1' (see the header).
export const armConfirmReset = (channel: number, pageId: number, target?: number | null): void => {
    if (!validRef(channel, pageId)) return;
    try {
        const stale: string[] = [];
        for (let i = 0; i < window.sessionStorage.length; i += 1) {
            const k = window.sessionStorage.key(i);
            if (k && k.indexOf(KEY_PREFIX) === 0) stale.push(k);
        }
        stale.forEach((k) => window.sessionStorage.removeItem(k));
        const value = target != null && Number.isInteger(target) && target > 0 ? String(target) : '1';
        window.sessionStorage.setItem(confirmResetKey(channel, pageId), value);
    } catch {
        // storage unavailable: the save gets 409 SOURCE_RESET_CONFIRM_REQUIRED and the dialog opens again
    }
};

export const clearConfirmReset = (channel: number | null | undefined, pageId: number | null | undefined): void => {
    if (!validRef(channel, pageId)) return;
    try {
        window.sessionStorage.removeItem(confirmResetKey(channel as number, pageId as number));
    } catch {
        // nothing to clear
    }
};
